import logoimg from '../../components/assets/img/bhajan-logo.jpg';
import { GraphDomain } from '../../types/graph.types';
import type { AppConfig } from '../types';

export const BM_CONFIG: AppConfig = {
  id: 'bm',
  apiApp: undefined,
  domain: GraphDomain.BhajanMala,
  browseFacetKeys: ['deity', 'ragam', 'talam', 'composer'],
  features: {
    bhajanBabiesSection: true,
    bhajanOfTheDay: true,
    todaysBhajans: true,
    collections: true,
    playlists: true,
    distraction: true,
    pronunciation: true,
    appDownload: true,
    projectModePromo: true,
    redirectUnknownToHome: false,
  },
  brand: {
    siteName: 'Bhajan Mala',
    wordmark: 'BhajanMala.com',
    logoSrc: logoimg,
    contactEmail: 'sgs.bhajanmala@gmail.com',
    aboutIntro:
      'Bhajan Mala is a devotional offering — a place to read, listen to, and dwell in the bhajans of Sri Ganapathy Sachchidananda Swamiji.',
  },
  skeleton: { baseColor: '#111827', highlightColor: '#1f2937' },
  browseHeroBackground:
    'radial-gradient(ellipse at 30% 0%, rgba(245,158,11,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 30%, rgba(99,102,241,0.10) 0%, transparent 55%), var(--bg-1)',
  storeUrls: {
    ios: 'https://apps.apple.com/us/app/bhajan-mala/id583564306',
    android: 'https://play.google.com/store/apps/details?id=org.dycusa.bhajanmala',
  },

  /**
   * /download copy. Every figure comes from GET /api/graph/index?platform=web4
   * measured 2026-07-23 — 2,160 bhajans, 2,011 tracks, 249 albums, 551 ragams,
   * 19 talams, 15 deities, 113 bhajan babies — rounded DOWN to a "+" so the
   * page stays true as the catalog grows.
   *
   * Screenshots are the ones on the live App Store listing (id583564306),
   * staged from appSpecific/bm/public/img/app/.
   */
  download: {
    heroBackground:
      'radial-gradient(ellipse at 15% 0%, rgba(245,158,11,0.13), transparent 55%), radial-gradient(ellipse at 85% 20%, rgba(99,102,241,0.14), transparent 55%), var(--bg-1)',
    closingBackground:
      'radial-gradient(ellipse at 50% 100%, rgba(245,158,11,0.10), transparent 60%)',
    eyebrow: 'for iPhone, iPad, Mac & Android',
    // "mala" is not a casual noun for the catalogue — it is the garland the app
    // is named for. Say what is actually in the app instead.
    headline: { lead: 'Every bhajan,', accent: 'in your pocket.' },
    // The albums are catalogued and linked, NOT downloaded — playback streams
    // from the listener's own Apple Music. Only the lyrics, swaras and meanings
    // come down onto the device, so only those are claimed here.
    lede:
      'Every bhajan Sri Swamiji composed — the lyrics, the swaras, the meanings — ' +
      'downloaded onto your phone and ready before the singing starts. No signal required.',
    // No album count. A big "240+ albums" next to "2,100+ bhajans" reads as a
    // music library you are being handed, and people took it to mean the app
    // gives away the album audio. The stats stay on what the app itself holds.
    // Four entries, not three: the row is grid-cols-2 / md:grid-cols-4, so an
    // odd count orphans one.
    stats: [
      { value: '2,100+', label: 'bhajans' },
      { value: '550+', label: 'ragams' },
      { value: '15', label: 'deities' },
      { value: '10', label: 'languages' },
    ],
    // Rating from the iTunes lookup API on 2026-07-23 (4.6 average). Left
    // un-counted so it does not need refreshing every time a review lands.
    rating: { score: '4.6', source: 'on the App Store' },
    // Only screenshots WITHOUT the red "Connect Apple Music" call-to-action.
    // The store's bhajan / lyrics / actions shots each lead with that button,
    // which reads as a sign-in wall when it is repeated down a gallery.
    //
    // lyrics-515.webp is NOT one of those: it is our own simulator capture of
    // bhajan 515, framed on the lyrics themselves so the Apple Music row above
    // them is out of shot. It replaces the album-cover grid, which showed the
    // one thing the app does not actually contain — the recordings are streamed
    // from Apple Music, so a wall of covers invited exactly the wrong reading.
    shots: [
      { src: '/img/app/home.webp', alt: 'The Bhajan Mala home screen, with a Browse button', caption: 'Home' },
      { src: '/img/app/browse.webp', alt: 'The browse screen: Instant Bhajan Finder, All Content, Recently Viewed, and categories for bhajans, stotras and mantras', caption: 'Browse' },
      { src: '/img/app/lyrics-515.webp', alt: 'The bhajan ambā bhavāni śāradē open in the app, showing its pallavi and caraṇaṁ in Roman script', caption: 'Lyrics' },
    ],
    /**
     * Only what someone downloading TODAY will actually find — the same rule the
     * `together` band follows. Deliberately absent:
     *   - the sruthi box / drone: on master, not in a shipped build.
     *   - satsang playlist templates: likewise (see `together` below).
     * Put either back only once it is live in a build on the stores, not when it
     * merges. The `tuning` icon is kept in DownloadIconKey and Download.tsx for
     * the sruthi entry's return.
     */
    features: {
      heading: 'What the app does that this website can’t',
      lede: 'The site is a window onto the library. The app hands you the library.',
      items: [
        {
          icon: 'offline',
          title: 'It works with no signal',
          body:
            'The whole library downloads onto your phone. In a temple hall, on a flight, ' +
            'mid-commute: it opens instantly and it opens completely.',
        },
        {
          icon: 'script',
          title: 'Ten languages, each in its own script',
          body:
            'Read in Telugu, Kannada, Hindi, Tamil and more, with the transliteration ' +
            'underneath. Download only the languages you want and keep them.',
        },
        {
          icon: 'search',
          title: 'Browse your way',
          body:
            'By ragam, talam, deity, composer or album. Jump straight to an id with the ' +
            'Instant Bhajan Finder, or search across every script at once.',
        },
        {
          icon: 'notes',
          title: 'Swaras above the line',
          body:
            'Toggle svaras on and the notation sits above each sung line, the way it is ' +
            'written on paper.',
        },
        {
          // Playback is Apple Music: the app catalogues the tracks and links them,
          // then streams through the listener's own subscription. It does not ship
          // the recordings, so the copy must not imply that it does.
          icon: 'wave',
          title: 'Every recording, linked',
          body:
            'Two thousand tracks across two hundred and forty albums, each tied to the ' +
            'bhajan it belongs to and streamed through Apple Music while you read along.',
        },
      ],
    },
    tablet: {
      src: '/img/app/ipad.webp',
      alt: 'Bhajan Mala on iPad, showing browse categories, ragams, albums, composers, collections, meanings and Live Satsang',
      heading: 'Bigger on iPad — and it runs on your Mac',
      body:
        'The same library, laid out for a larger screen. On an Apple silicon Mac it installs ' +
        'straight from the App Store and runs as a desktop app.',
    },
    languages: {
      heading: 'Sing in your own script',
      body:
        'Pick the languages you read. Each one downloads as its own pack and stays on the device — ' +
        'switch between them mid-bhajan without losing your place.',
      endonyms: ['తెలుగు', 'ಕನ್ನಡ', 'हिन्दी', 'தமிழ்', 'മലയാളം', 'বাংলা', 'ଓଡ଼ିଆ', 'ਪੰਜਾਬੀ', 'ગુજરાતી', 'English'],
    },
    /**
     * Deliberately excludes satsang playlist templates: they are on master but
     * not in a shipped build, and this page only promises what someone
     * downloading today will actually find.
     */
    together: {
      heading: 'Built for singing together',
      lede: 'A bhajan is rarely a solo activity. Neither is the app.',
      items: [
        {
          icon: 'broadcast',
          title: 'Live satsang channels',
          body: 'One person leads and everyone else’s screen follows, page for page. Nobody loses the place.',
        },
        {
          icon: 'list',
          title: 'Playlists for the sitting',
          body: 'Line up the bhajans before you sit down, in the order you mean to sing them.',
        },
        {
          icon: 'star',
          title: 'Bhajan Babies',
          body: 'Over a hundred animated bhajans made for the youngest singers in the room.',
        },
      ],
    },
    closing: {
      heading: 'Take it with you',
      body: 'The next time a bhajan comes to mind, it will already be there.',
    },
  },
};
